import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiErr, fmtDate } from "../SuperHeader";
import { useAdminFetch, TabState } from "../useAdminFetch";
import ConfirmDialog from "../ConfirmDialog";

const ROLE = { company_admin: "Company Admin", sales_associate: "Sales Associate" };

function UserRow({ u, ask }) {
  const active = u.is_active !== false;
  return (
    <tr className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
      <td className="p-3"><div className="font-medium">{u.name}</div><div className="text-[11px] text-slate-500">{u.email}</div></td>
      <td className="p-3">
        <Select value={u.role} onValueChange={(v) => v !== u.role && ask("role", u, v)}>
          <SelectTrigger className="w-44 h-8" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{Object.entries(ROLE).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="p-3"><Badge className={active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"} data-testid={`user-status-${u.id}`}>{active ? "Active" : "Inactive"}</Badge></td>
      <td className="p-3 text-xs text-slate-500">{fmtDate(u.last_login_at)}</td>
      <td className="p-3 text-right whitespace-nowrap space-x-1">
        <Button size="sm" variant="outline" onClick={() => ask("toggle", u)} data-testid={`user-toggle-${u.id}`}>{active ? "Deactivate" : "Activate"}</Button>
        <Button size="sm" variant="ghost" onClick={() => ask("reset", u)} data-testid={`user-reset-${u.id}`}><KeyRound className="w-3 h-3 mr-1" />Reset password</Button>
      </td>
    </tr>
  );
}

export default function UsersTab({ cid }) {
  const { data, error, loading, reload } = useAdminFetch(`/admin/companies/${cid}/users`);
  const [confirm, setConfirm] = useState(null);
  const exec = (fn, ok) => async () => {
    try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  const ask = (kind, u, role) => {
    const url = `/admin/companies/${cid}/users/${u.id}`;
    const active = u.is_active !== false;
    if (kind === "role") setConfirm({ title: `Change ${u.name}'s role to ${ROLE[role]}?`, label: "Change role", run: exec(() => api.patch(url, { role }), "Role updated") });
    if (kind === "toggle") setConfirm({ title: `${active ? "Deactivate" : "Activate"} ${u.name}?`, body: active ? "They will be signed out and unable to log in." : "They will be able to log in again.", danger: active, label: active ? "Deactivate" : "Activate", run: exec(() => api.patch(url, { is_active: !active }), active ? "User deactivated" : "User activated") });
    if (kind === "reset") setConfirm({ title: `Email a password reset code to ${u.email}?`, label: "Send reset email", run: exec(() => api.post(`${url}/reset-password`), "Reset code emailed") });
  };
  return (
    <Card className="p-4 border-slate-200" data-testid="users-tab">
      <TabState loading={loading} error={error} empty={data?.length === 0} emptyText="This company has no users." testid="users">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600"><tr><th className="p-3 text-left">User</th><th className="p-3 text-left">Role</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Last login</th><th className="p-3 text-right">Actions</th></tr></thead>
            <tbody>{(data || []).map((u) => <UserRow key={u.id} u={u} ask={ask} />)}</tbody>
          </table>
        </div>
      </TabState>
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </Card>
  );
}
